import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type Provider = 'gemini' | 'openai' | 'xai' | 'anthropic' | 'byteplus' | 'elevenlabs' | 'meshy' | 'uniblock';

export interface KeyRecord {
  provider: Provider;
  /** Wrapped key bytes (AES-GCM ciphertext of the raw API string). */
  cipher: ArrayBuffer;
  iv: ArrayBuffer;
  createdAt: number;
  label?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  attachments?: Attachment[];
  meta?: Record<string, unknown>;
  createdAt: number;
}

export interface Attachment {
  id: string;
  kind: 'image' | 'video' | 'audio' | 'mask' | 'pose' | 'glb' | 'file';
  mime: string;
  /** Blob is stored directly in IDB. */
  blob: Blob;
  width?: number;
  height?: number;
  durationMs?: number;
  /** Optional thumbnail blob. */
  thumb?: Blob;
  name?: string;
  source?: string;
}

export interface ChatSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  pinned?: boolean;
  cloudSyncedAt?: number;
}

export interface CharacterTemplate {
  id: string;
  name: string;
  prompt: string;
  refs: Attachment[];
  variants?: Attachment[];
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export interface SceneSaver {
  id: string;
  name: string;
  prompt: string;
  refs: Attachment[];
  cameraNotes?: string;
  lightingNotes?: string;
  createdAt: number;
  updatedAt: number;
}

export interface GeneratedAsset {
  id: string;
  sessionId?: string;
  kind: 'image' | 'video';
  prompt: string;
  modelId: string;
  blob: Blob;
  width: number;
  height: number;
  durationMs?: number;
  createdAt: number;
  cost?: { provider: Provider; usd?: number };
}

export interface TaskRecord {
  id: string;            // local id
  remoteId?: string;     // provider task id
  provider: Provider;
  kind: 'image' | 'video' | 'edit' | 'bg' | 'lipsync' | 'retarget';
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  prompt?: string;
  request: unknown;
  response?: unknown;
  error?: string;
  sessionId?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Settings {
  id: 'settings';
  theme: 'light' | 'dark' | 'system';
  cloudSync: boolean;
  defaultImageModel?: string;
  defaultVideoModel?: string;
  defaultAspect?: string;
  defaultDurationSec?: number;
  donateUrl?: string;
  personaOverride?: string;
}

interface ProtoDB extends DBSchema {
  keys: { key: Provider; value: KeyRecord };
  sessions: { key: string; value: ChatSession; indexes: { 'by-updated': number } };
  messages: { key: string; value: ChatMessage & { sessionId: string }; indexes: { 'by-session': string } };
  characters: { key: string; value: CharacterTemplate; indexes: { 'by-updated': number } };
  scenes: { key: string; value: SceneSaver; indexes: { 'by-updated': number } };
  assets: { key: string; value: GeneratedAsset; indexes: { 'by-session': string; 'by-created': number } };
  tasks: { key: string; value: TaskRecord; indexes: { 'by-status': string; 'by-session': string } };
  settings: { key: 'settings'; value: Settings };
  meta: { key: string; value: { id: string; value: unknown } };
}

let dbp: Promise<IDBPDatabase<ProtoDB>> | null = null;

export function db() {
  if (!dbp) {
    dbp = openDB<ProtoDB>('protolive', 1, {
      upgrade(d) {
        d.createObjectStore('keys', { keyPath: 'provider' });
        const s = d.createObjectStore('sessions', { keyPath: 'id' });
        s.createIndex('by-updated', 'updatedAt');
        const m = d.createObjectStore('messages', { keyPath: 'id' });
        m.createIndex('by-session', 'sessionId');
        const c = d.createObjectStore('characters', { keyPath: 'id' });
        c.createIndex('by-updated', 'updatedAt');
        const sc = d.createObjectStore('scenes', { keyPath: 'id' });
        sc.createIndex('by-updated', 'updatedAt');
        const a = d.createObjectStore('assets', { keyPath: 'id' });
        a.createIndex('by-session', 'sessionId');
        a.createIndex('by-created', 'createdAt');
        const t = d.createObjectStore('tasks', { keyPath: 'id' });
        t.createIndex('by-status', 'status');
        t.createIndex('by-session', 'sessionId');
        d.createObjectStore('settings', { keyPath: 'id' });
        d.createObjectStore('meta', { keyPath: 'id' });
      }
    });
  }
  return dbp;
}

export async function getSettings(): Promise<Settings> {
  const d = await db();
  const s = await d.get('settings', 'settings');
  return s ?? { id: 'settings', theme: 'dark', cloudSync: false };
}

export async function setSettings(patch: Partial<Settings>): Promise<Settings> {
  const d = await db();
  const cur = await getSettings();
  const next: Settings = { ...cur, ...patch, id: 'settings' };
  await d.put('settings', next);
  return next;
}

export async function listSessions(): Promise<ChatSession[]> {
  const d = await db();
  const all = await d.getAllFromIndex('sessions', 'by-updated');
  return all.reverse();
}

export async function listMessages(sessionId: string): Promise<ChatMessage[]> {
  const d = await db();
  const all = await d.getAllFromIndex('messages', 'by-session', sessionId);
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function putMessage(sessionId: string, m: ChatMessage): Promise<void> {
  const d = await db();
  await d.put('messages', { ...m, sessionId });
  await d.put('sessions', {
    ...(await d.get('sessions', sessionId))!,
    updatedAt: Date.now()
  });
}

export async function deleteMessage(messageId: string): Promise<void> {
  const d = await db();
  await d.delete('messages', messageId);
}

export async function newSession(title = 'New session'): Promise<ChatSession> {
  const d = await db();
  const s: ChatSession = {
    id: crypto.randomUUID(),
    title,
    createdAt: Date.now(),
    updatedAt: Date.now()
  };
  await d.put('sessions', s);
  return s;
}